import { GuestTicketTier } from "../types";

export interface CsvGuestDraft {
  id: string;
  full_name: string;
  email: string;
  ticket_tier: GuestTicketTier;
  plus_one_limit: number;
}

export interface CsvGuestFieldErrors {
  full_name?: string;
  email?: string;
  plus_one_limit?: string;
}

export interface CsvParseResult {
  guests: CsvGuestDraft[];
  errors: string[];
}

const NAME_HEADERS = ["full_name", "fullname", "full name", "name"];
const EMAIL_HEADERS = ["email", "email address"];

const splitCsvLine = (line: string): string[] => {
  const values: string[] = [];
  let current = "";
  let inQuotes = false;

  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    const next = line[i + 1];

    if (char === '"') {
      if (inQuotes && next === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
      continue;
    }

    if (char === "," && !inQuotes) {
      values.push(current.trim());
      current = "";
      continue;
    }

    current += char;
  }

  values.push(current.trim());
  return values;
};

const findColumnIndex = (headers: string[], candidates: string[]) => {
  const normalized = headers.map((header) => header.toLowerCase().trim());
  for (const candidate of candidates) {
    const index = normalized.indexOf(candidate);
    if (index >= 0) return index;
  }
  return -1;
};

const createDraftId = (index: number) => `csv-guest-${index}-${Date.now()}`;

export const parseGuestCsv = (text: string): CsvParseResult => {
  const lines = text
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);

  if (lines.length === 0) {
    return { guests: [], errors: ["CSV file is empty"] };
  }

  const headers = splitCsvLine(lines[0]);
  const nameIndex = findColumnIndex(headers, NAME_HEADERS);
  const emailIndex = findColumnIndex(headers, EMAIL_HEADERS);

  if (nameIndex < 0) {
    return {
      guests: [],
      errors: ['CSV must include a "full_name" or "name" column'],
    };
  }

  const guests: CsvGuestDraft[] = [];
  const errors: string[] = [];

  lines.slice(1).forEach((line, offset) => {
    const rowNumber = offset + 2;
    const values = splitCsvLine(line);
    const fullName = values[nameIndex]?.trim() || "";
    const email = emailIndex >= 0 ? values[emailIndex]?.trim() || "" : "";

    if (!fullName) {
      errors.push(`Row ${rowNumber}: Full name is required`);
      return;
    }

    guests.push({
      id: createDraftId(guests.length),
      full_name: fullName,
      email,
      ticket_tier: "party_not_presents",
      plus_one_limit: 0,
    });
  });

  if (guests.length === 0 && errors.length === 0) {
    errors.push("CSV has headers but no guest rows");
  }

  return { guests, errors };
};

export const isValidEmail = (email: string) =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

export const validateGuestDraft = (
  guest: CsvGuestDraft,
  allGuests: CsvGuestDraft[],
  existingGuests: Array<{ full_name: string; email?: string | null }> = []
): CsvGuestFieldErrors => {
  const errors: CsvGuestFieldErrors = {};
  const name = guest.full_name.trim();

  if (!name) {
    errors.full_name = "Full name is required";
  }

  const email = guest.email.trim();
  if (email && !isValidEmail(email)) {
    errors.email = "Enter a valid email address";
  }

  if (guest.ticket_tier === "inner_circle_plus_more") {
    if (guest.plus_one_limit < 0 || guest.plus_one_limit > 10) {
      errors.plus_one_limit = "Plus-one allowance must be between 0 and 10";
    }
  }

  if (name) {
    const normalizedName = name.toLowerCase();
    const duplicateName = allGuests.some(
      (other) => other.id !== guest.id && other.full_name.trim().toLowerCase() === normalizedName
    );
    if (duplicateName) {
      errors.full_name = "Duplicate name in this upload";
    }

    const existingName = existingGuests.some(
      (other) => other.full_name.trim().toLowerCase() === normalizedName
    );
    if (existingName) {
      errors.full_name = "This guest is already on the invite list";
    }
  }

  if (email) {
    const normalizedEmail = email.toLowerCase();
    const duplicateEmail = allGuests.some(
      (other) =>
        other.id !== guest.id && other.email.trim().toLowerCase() === normalizedEmail
    );
    if (duplicateEmail) {
      errors.email = "Duplicate email in this upload";
    }

    const existingEmail = existingGuests.some(
      (other) => other.email?.trim().toLowerCase() === normalizedEmail
    );
    if (existingEmail) {
      errors.email = "This email is already on the invite list";
    }
  }

  return errors;
};

export const validateAllGuestDrafts = (
  guests: CsvGuestDraft[],
  existingGuests: Array<{ full_name: string; email?: string | null }> = []
) => {
  const rowErrors = guests.map((guest) =>
    validateGuestDraft(guest, guests, existingGuests)
  );
  const hasErrors = rowErrors.some((errors) => Object.keys(errors).length > 0);
  return { rowErrors, hasErrors, isValid: guests.length > 0 && !hasErrors };
};

export const applyDefaultsToGuests = (
  guests: CsvGuestDraft[],
  defaults: {
    ticket_tier: CsvGuestDraft["ticket_tier"];
    plus_one_limit: number;
  },
  customizePlusOnes: boolean
): CsvGuestDraft[] =>
  guests.map((guest) => ({
    ...guest,
    ticket_tier: defaults.ticket_tier,
    plus_one_limit:
      defaults.ticket_tier === "inner_circle_plus_more"
        ? customizePlusOnes
          ? guest.plus_one_limit
          : defaults.plus_one_limit
        : 0,
  }));
